<template>
  <vd-error
    v-if="!queryLoading && notFound"
    status="404"
    message="The metric you are looking for does not exist"
    :back-link="backLink"
  />
  <vd-error
    v-else-if="!queryLoading && featureId && !inWIPVersion && !preview"
    status="405"
    message="You can not edit the old version of a metric"
    :back-link="backLink"
  />
  <vd-error
    v-else-if="!queryLoading && !featureId && !inWIPVersion"
    status="405"
    message="You can not link a metric in an old version"
    :back-link="backLink"
  />
  <div v-else class="payment-feature-form-page">
    <vd-page-form
      class="payment-feature-form"
      :preview="preview"
      :title="pageTitle"
      :back-link="backLink"
      :edit-link="editLink"
      :form="form"
      :hide-actions="!inWIPVersion || !canWriteFeatures"
      :save-callback="saveForm"
      :save-loading="updateLoading || createLoading"
      :loading="queryLoading"
      :allow-delete="!!featureId && canWriteFeatures"
      @delete="onDelete"
    >
      <h2 class="payment-feature-form-title">Main info</h2>
      <br />
      <VdFormField
        v-model="form.name"
        label="Metric name"
        prop="name"
        type="text"
        required
        show-word-limit
        max-length="40"
        placeholder="NoAds purchases"
        :disabled="!!featureId"
      />
      <br />
      <VdFormField
        v-if="preview || edit"
        v-model="form.id"
        prop="id"
        type="text"
        label="VoodooSauce keyword"
        required
        :disabled="!!featureId"
        show-word-limit
        max-length="40"
        placeholder="VSauce ID"
        :error="voodooSauceKeyWordErrorMessage"
        @input="voodooSauceKeyWordErrorMessage = ''"
      />
      <br />
      <VdFormField
        v-model="form.description"
        prop="description"
        type="textarea"
        label="Description"
        placeholder="ex: need to know the best AdFreq"
        show-word-limit
        max-length="150"
        :auto-size="{ minRows: 3, maxRows: 4 }"
      />
      <br />

      <h2 class="payment-feature-form-title">Measurement</h2>
      <br />
      <VdFormField label="Window" prop="windowUnit" required>
        <VdSelect
          v-model="form.windowUnit"
          :choices="windowUnitChoices"
          :disabled="preview"
          @change="onWindowUnitChange"
        />
      </VdFormField>
      <div class="payment-feature-form-window-size">
        <VdFormField
          v-model="form.windowSize"
          type="number"
          min="1"
          :disabled="isLifetime || preview"
          :label="form.windowUnit === 'DAYS' ? 'days' : 'number of transactions'"
          prop="windowSize"
          :required="!isLifetime"
          :placeholder="`Number of ${form.windowUnit === 'DAYS' ? 'days' : 'transactions'}`"
        />
        <div v-if="form.windowUnit === 'DAYS' && form.measurementMethod !== 'DAILY_AVG'">
          <Checkbox
            v-model="isLifetime"
            :binary="true"
            name="isLifetime"
            :disabled="preview"
            class="payment-feature-checkbox"
          />
          <label for="isLifetime">Lifetime</label>
        </div>
      </div>

      <VdFormField label="Measurement method" prop="measurementMethod" required>
        <VdSelect
          v-model="form.measurementMethod"
          :choices="measurementMethodChoices"
          :disabled="preview"
          @change="onMeasurementMethodChange"
        />
      </VdFormField>
      <VdFormField label="Data type" prop="type">
        <VdSelect v-model="form.type" :choices="featureTypeChoices" disabled />
      </VdFormField>

      <h2 class="payment-feature-form-title">Filters</h2>
      <br />
      <VdFormField label="Add to category" prop="category">
        <VdSelect v-model="form.category" :choices="featureCategoryChoices" />
      </VdFormField>
      <br />
      <div class="payment-feature-form-exclude-iaps">
        <Checkbox
          v-model="excludeOrIncludeIaps"
          :binary="true"
          name="excludeOrIncludeIaps"
          :disabled="preview"
          class="payment-feature-checkbox"
          @change="onExcludeOrIncludeIapsChange"
        />
        <label for="excludeOrIncludeIaps">Exclude/include IAPs</label>
      </div>
      <br />
      <div v-if="excludeOrIncludeIaps" style="margin-bottom: 0.75rem">
        <RadioButton
          v-model="form.iapFilterType"
          name="excludeIap"
          value="EXCLUDE"
          class="payment-feature-checkbox"
          :disabled="preview"
        />
        <label for="excludeIap" class="ml-2">Exclude</label>
        <RadioButton
          v-model="form.iapFilterType"
          name="includeIap"
          value="INCLUDE_ONLY"
          class="payment-feature-checkbox"
          style="margin-left: 1rem"
          :disabled="preview"
        />
        <label for="includeIap" class="ml-2">Include</label>
      </div>
      <br />
      <VdFormField v-if="excludeOrIncludeIaps" label="Select iOS IAPs" prop="iaps.ios" required>
        <IAPSelectDropdown
          :model-values="form.iaps?.ios"
          :platform="Platform.Ios"
          :game-id="gameId"
          multiple
          :disabled="preview"
          :version-id="versionId"
          @update-multiple="(value) => handleIapIdsChange(value, Platform.Ios)"
        />
      </VdFormField>
      <VdFormField v-if="excludeOrIncludeIaps" label="Select android IAPs" prop="iaps.android" required>
        <IAPSelectDropdown
          :model-values="form.iaps?.android"
          :platform="Platform.Android"
          :game-id="gameId"
          multiple
          :disabled="preview"
          :version-id="versionId"
          @update-multiple="(value) => handleIapIdsChange(value, Platform.Android)"
        />
      </VdFormField>
    </vd-page-form>

    <!-- warning delete dialog -->
    <vd-dialog
      :visible="displayDeleteWarning"
      title="This feature can't be deleted"
      type="error"
      @close="closeDeleteWarning"
    >
      <span>
        You can't delete this feature as it is used
        <strong>in the following segments:</strong>
      </span>
      <ul class="delete-warning-list">
        <li v-for="segment in deleteWarningDetails.segments" :key="segment">
          {{ segment }}
        </li>
      </ul>
      <template #footer>
        <vd-button type="secondary" skin="blue" @click="closeDeleteWarning">I understand</vd-button>
      </template>
    </vd-dialog>
  </div>
</template>

<script setup lang="ts">
import { VdFormField, VdSelect } from "@voodoo.io/voodoo-ui";
import Checkbox from "primevue/checkbox";
import RadioButton from "primevue/radiobutton";
import { computed, reactive, ref, watch } from "vue";
import { useRouter } from "vue-router";

import IAPSelectDropdown from "@/components/IAPSelectDropdown";
import {
  Feature,
  FeatureType,
  Platform,
  featureTypeChoices,
  useDeleteFeatureMutation,
  useFeatureFormPageCreateMutation,
  useFeatureFormPageQuery,
  useFeatureFormPageSaveMutation,
  useMutationResponse,
} from "@/graphql";
import usePermissions from "@/hooks/use-permissions";
import links from "@/router/links";
import { featureCategoryChoices, windowUnitChoices } from "./constants";

const props = defineProps({
  gameId: {
    type: String,
    required: true,
  },
  versionId: {
    type: String,
    required: true,
  },
  featureId: {
    type: String,
    default: null,
    required: false,
  },
  preview: {
    type: Boolean,
    default: false,
  },
  edit: {
    type: Boolean,
    default: false,
  },
});

const router = useRouter();
const { canWriteFeatures } = usePermissions();

interface PaymentFeatureForm {
  id: string | null;
  name: string;
  description: string;
  windowUnit: string;
  windowSize: number | null;
  measurementMethod: string;
  type: FeatureType;
  category: string;
  iapFilterType: string | null;
  iaps: {
    ios: string[];
    android: string[];
  } | null;
  source: string;
}

const form = reactive<PaymentFeatureForm>({
  id: null, // VoodooSauce keyword
  name: "",
  description: "",
  windowUnit: "DAYS",
  windowSize: null,
  measurementMethod: "",
  type: FeatureType.Float,
  category: "",
  iapFilterType: null,
  iaps: null,
  source: "payment_custom",
});

const measurementMethodChoices = computed(() => [
  ...(form.windowUnit === "DAYS"
    ? [{ label: "Sum of transaction spend ($)", value: "SUM_OF_TRANSACTIONS", type: FeatureType.Float }]
    : []),
  ...(form.windowUnit === "DAYS"
    ? [{ label: "Average purchase spend per day ($)", value: "DAILY_AVG", type: FeatureType.Float }]
    : []),
  { label: "Average purchase spend per transaction ($)", value: "AVG_TRANSACTION_AMOUNT", type: FeatureType.Float },
  { label: "Median purchase spend per transaction ($)", value: "MEDIAN_TRANSACTION_AMOUNT", type: FeatureType.Float },
  ...(form.windowUnit === "NUMBER_OF_TRANSACTIONS"
    ? [{ label: "Maximum transaction spend ($)", value: "MAX_TRANSACTION_AMOUNT", type: FeatureType.Float }]
    : []),
  ...(form.windowUnit === "NUMBER_OF_TRANSACTIONS"
    ? [{ label: "Minimum transaction spend ($)", value: "MIN_TRANSACTION_AMOUNT", type: FeatureType.Float }]
    : []),
  { label: "Total number of transactions", value: "TRANSACTION_NUMBER", type: FeatureType.Integer },
  { label: "Has purchased", value: "HAS_PURCHASED", type: FeatureType.Boolean },
]);

const voodooSauceKeyWordErrorMessage = ref("");
const isLifetime = ref(false);
const excludeOrIncludeIaps = ref(false);

const { result, loading: queryLoading } = useFeatureFormPageQuery(
  {
    gameId: props.gameId,
    versionId: props.versionId,
    featureId: props.featureId,
  },
  { fetchPolicy: "network-only" }
);

const feature = computed(() => result.value?.game?.version?.feature);
const inWIPVersion = computed(() => result.value?.game?.version?.isWip);
const notFound = computed(() => !queryLoading.value && props.featureId && !feature.value);

const pageTitle = computed(() => {
  if (props.featureId) {
    return (feature.value && feature.value.name) || "";
  }
  return "New payment metric";
});

watch(feature, (value: Feature) => {
  if (value) {
    form.id = value.id;
    form.name = value.name;
    form.type = value.type;
    form.description = value.description ?? "";
    form.source = value.source;
    form.category = value.category ?? "";
    form.windowUnit = value.windowUnit ?? "";
    form.windowSize = value.windowSize ?? null;
    form.measurementMethod = value.measurementMethod ?? "";
    form.iapFilterType = value.iapFilterType ?? null;
    form.iaps = null;

    if (value.iaps !== null) {
      form.iaps = {
        ios: value.iaps?.ios ?? [],
        android: value.iaps?.android ?? [],
      };
    }

    if (form.iapFilterType) {
      excludeOrIncludeIaps.value = true;
    }

    if (form.windowUnit === "DAYS" && form.windowSize === null) {
      isLifetime.value = true;
    }
  }
});

const backLink = { name: links.games.features.list, params: { gameId: props.gameId, versionId: props.versionId } };

const editLink = {
  name: links.games.features.editPayment,
  params: { gameId: props.gameId, versionId: props.versionId, featureId: props.featureId },
};

interface Iap {
  ids: string[];
}

const handleIapIdsChange = (value: Iap, platform: Platform) => {
  if (platform === Platform.Ios) {
    form.iaps = {
      ios: value.ids,
      android: form.iaps?.android ?? [],
    };
  } else {
    form.iaps = {
      ios: form.iaps?.ios ?? [],
      android: value.ids,
    };
  }
};

/**
 * Update the feature
 */
const { mutate: updateFeature, loading: updateLoading } = useFeatureFormPageSaveMutation({});

const onFeatureUpdate = async () => {
  const response = await updateFeature({
    gameId: props.gameId,
    versionId: props.versionId,
    featureId: props.featureId,
    input: {
      ...form,
    },
  });

  const updateResult = useMutationResponse(response, (resp) => resp?.saveFeature);

  if (updateResult?.success) {
    router.push(backLink);
  } else if (updateResult?.isConflict) {
    voodooSauceKeyWordErrorMessage.value = "The technical name is already taken";
  }
};

/**
 * Creature the feature
 */
const { mutate: createFeature, loading: createLoading } = useFeatureFormPageCreateMutation({});

const onFeatureCreate = async () => {
  const response = await createFeature({
    gameId: props.gameId,
    versionId: props.versionId,
    input: form,
  });

  const saveResult = useMutationResponse(response, (resp) => resp?.createFeature);

  if (saveResult?.success) {
    router.push(backLink);
  }
};

const saveForm = async () => {
  if (props.featureId) {
    await onFeatureUpdate();
  } else {
    await onFeatureCreate();
  }
};

/**
 * Delete the feature
 */
const displayDeleteWarning = ref(false);

const deleteWarningDetails = reactive<{ segments: string[] }>({
  segments: [],
});

const closeDeleteWarning = () => {
  displayDeleteWarning.value = false;
};

const { mutate: deleteFeature } = useDeleteFeatureMutation({});

const onDelete = async () => {
  if (!props.featureId) {
    return;
  }

  const response = await deleteFeature({
    gameId: props.gameId,
    versionId: props.versionId,
    featureId: props.featureId,
  });

  const deleteResult = useMutationResponse(response, (resp) => resp?.deleteFeature);

  if (deleteResult?.success) {
    router.push(backLink);
  } else if (deleteResult?.isCurrentlyUsed) {
    deleteWarningDetails.segments = deleteResult?.usedInSegments ?? [];
    displayDeleteWarning.value = true;
  }
};

watch(isLifetime, (value: boolean) => {
  if (value) {
    form.windowSize = null;
  } else {
    form.windowSize = 1;
  }
});

const onExcludeOrIncludeIapsChange = () => {
  if (!excludeOrIncludeIaps.value) {
    form.iapFilterType = null;
    form.iaps = null;
  } else {
    form.iapFilterType = "EXCLUDE";
  }
};

watch(
  () => form.measurementMethod,
  (value: string) => {
    form.type = measurementMethodChoices.value.find((choice) => choice.value === value)?.type ?? FeatureType.Float;
  }
);

const onWindowUnitChange = (value: string) => {
  form.windowUnit = value;
  form.measurementMethod = "";
  isLifetime.value = false;
};

const onMeasurementMethodChange = (value: string) => {
  if (value === "DAILY_AVG") {
    form.windowSize = null;
    isLifetime.value = false;
  }
};
</script>

<style lang="scss">
.payment-feature-form {
  .payment-feature-form-title {
    margin-bottom: 1rem;
  }

  .payment-feature-form-window-size {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .payment-feature-checkbox {
    margin-right: 0.5rem;
  }

  .payment-feature-form-exclude-iaps {
    display: flex;
    align-items: center;
    margin-bottom: 1rem;
  }

  .vd-form__content {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    column-gap: 40px;
  }

  .el-select {
    width: 100%;
  }
}
</style>
